// utils/fileUtils.js

import mammoth from 'mammoth';
import { fileTypeFromBuffer } from 'file-type';
import Tesseract from 'tesseract.js';

export const readDOCX = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const { value: text } = await mammoth.extractRawText({ arrayBuffer });
    console.log('Extracted text from DOCX:', text);
    return text;
  } catch (error) {
    console.error('Error reading DOCX file:', error);
    throw new Error('Failed to read the DOCX file.');
  }
};

export const readImage = async (file) => {
  try {
    const result = await Tesseract.recognize(file, 'eng', {
      logger: (m) => console.log(m),
    });
    const text = result.data.text;
    console.log('Extracted text from image:', text);
    return text;
  } catch (error) {
    console.error('Error reading image file:', error);
    throw new Error('Failed to read the image file.');
  }
};

export const readPlainText = async (file) => {
  try {
    const text = await file.text();
    console.log('Plain text file content:', text);
    return text;
  } catch (error) {
    console.error('Error reading plain text file:', error);
    throw new Error('Failed to read the text file.');
  }
};

export const readFileContent = async (file, assistantType = null) => {
  try {
    console.log('Reading file:', file.name, 'for assistant:', assistantType);
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const type = await fileTypeFromBuffer(uint8Array);

    console.log('Detected file type:', type);

    // For Nur Al Huda, allow all file types since it uses Prompt-ID framework
    const isNurAlHuda = assistantType === 'nurAlHuda';
    
    if (!isNurAlHuda) {
      // For other assistants, keep the original restrictions
      if (type?.mime === 'application/pdf') {
        console.log('PDF files are not supported for this assistant.');
        throw new Error('PDF files are not supported at this time.');
      } else if (
        type?.mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        return await readDOCX(file);
      } else if (type?.mime.startsWith('image/')) {
        return await readImage(file);
      } else if (type?.mime === 'text/plain') {
        return await readPlainText(file);
      } else {
        console.log('Unsupported file type for this assistant.');
        throw new Error('Unsupported file type.');
      }
    } else {
      // For Nur Al Huda, handle all supported file types
      if (type?.mime === 'application/pdf') {
        console.log('PDF files are supported for Nur Al Huda.');
        // For now, return a placeholder since PDF reading isn't implemented
        return `[PDF file: ${file.name}] - Content will be processed by the AI framework.`;
      } else if (
        type?.mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        type?.mime === 'application/msword'
      ) {
        return await readDOCX(file);
      } else if (type?.mime.startsWith('image/')) {
        return await readImage(file);
      } else if (
        type?.mime === 'text/plain' ||
        type?.mime === 'text/csv' ||
        type?.mime === 'application/json' ||
        type?.mime === 'text/markdown' ||
        type?.mime === 'text/html' ||
        type?.mime === 'text/css' ||
        type?.mime === 'application/javascript' ||
        type?.mime === 'text/xml' ||
        type?.mime === 'application/xml'
      ) {
        return await readPlainText(file);
      } else {
        console.log('File type supported by Nur Al Huda framework:', type?.mime);
        // For other file types, let the AI framework handle them
        return `[File: ${file.name} (${type?.mime || 'unknown type'})] - Content will be processed by the AI framework.`;
      }
    }
  } catch (error) {
    console.error('Error reading file:', error);
    throw new Error('Failed to read the file.');
  }
};

/*
  const readPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    let text = '';
    for (const page of pages) {
      const textContent = await page.getTextContent();
      text += textContent.items.map((item) => item.str).join(' ');
    }
    return text;
  };
  */